import { ISimpleEventDetail, PriceType, SupportedLanguage } from "interfaces";
import moment from "moment";
import { languageSchema } from "routes/event/schema";
import { z } from "zod";

export const ParentTicketSchema = (eventDetail: ISimpleEventDetail) => {
  return z.discriminatedUnion("is_package", [
    z.object({
      is_package: z.literal(true),
      category: languageSchema(eventDetail.languages),
      selling_min_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      selling_max_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      selling_type: z.string().min(1, "This field is required"),
      attendant_template: z.array(
        z.object({
          template_id: z.string().min(1, "This field is required"),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ).min(2, "At least 2 attendant templates are required for a package ticket"),
    }),
    z.object({
      is_package: z.literal(false),
      category: languageSchema(eventDetail.languages),
      selling_min_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      selling_max_qty: z
        .number({
          invalid_type_error: "Quantity must be a number",
          required_error: "This field is required"
        })
        .min(1, {message:"Quantity must be equal or greater than 1"})
        .nonnegative({ message: "Quantity must be a positive number" }),
      selling_type: z.string().min(1, "This field is required"),
      attendant_template: z.array(
        z.object({
          template_id: z.string().min(1, "This field is required"),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ).min(1, "This field is required"),
    })
  ])
  .refine((data) => data.selling_min_qty <= data.selling_max_qty, {
    message: "Max quantity cannot be smaller than min quantity.",
    path: ["selling_max_qty"],
  })
  .superRefine(({selling_min_qty, selling_max_qty},ctx) => {
    if (selling_max_qty < selling_min_qty) {
      ctx.addIssue({
        path: ["selling_max_qty"],
        code: "custom",
        message: "Selling max quantity cannot be smaller than Selling min quantity",
      });
    }
  })
}

export const SubTicketSchema = (eventDetail: ISimpleEventDetail) => {

  const ticketSchema = (priceType: PriceType) => {
    return z.object({
      price_type: z.literal(priceType),
      type_name: languageSchema(eventDetail.languages),
      short_description: z.object({
        en: z.string().optional(),
        vi: z.string().optional()
      }),
      original_price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      sell_from: z.string().min(1, "This field is required"),
      sell_to: z.string().min(1, "This field is required"),
      hide_in_ui: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      total_for_sell: z.union([z.number().int().min(0, "Quantity must be equal or greater than 0").nullable(), z.nan()]).optional(),
      selling_days: z.array(z.string()).min(1, "This field is required"),
      weekly_event_days: z.array(z.string()).min(1, "This field is required"),
      sold_out_before: z.string().optional(),
      detailed_ticket_information: languageSchema(eventDetail.languages, true),
      showtime: z.string().min(1, "This field is required"),
      pricing_by_date_type: z.object({
        holiday: z.object({
          price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
          original_price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
        }),
        weekend: z.object({
          price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
          original_price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
        }),
      }).optional(),
    })
  };

  return z.discriminatedUnion("price_type",[
    ticketSchema(PriceType.FREE),
    ticketSchema(PriceType.PAID)
  ])
  .refine((data) => moment(data.sell_from).isSameOrBefore(moment(data.sell_to)), {
    message: "End date cannot be earlier than start date.",
    path: ["sell_to"],
  })
  .refine((data) => data.original_price >= data.price, {
    message: "Original price cannot be smaller than sell price.",
    path: ["original_price"],
  })
  .superRefine((data, ctx) => {
    if (eventDetail.date_pricing?.enabled && data.pricing_by_date_type) {
      if (
        typeof data.pricing_by_date_type.holiday?.original_price === "number" &&
        typeof data.pricing_by_date_type.holiday?.price === "number" &&
        data.pricing_by_date_type.holiday.original_price < data.pricing_by_date_type.holiday.price
      ) {
        ctx.addIssue({
          path: ["pricing_by_date_type", "holiday", "original_price"],
          code: "custom",
          message: "Holiday original price cannot be smaller than holiday price.",
        });
      }
      if (
        typeof data.pricing_by_date_type.weekend?.original_price === "number" &&
        typeof data.pricing_by_date_type.weekend?.price === "number" &&
        data.pricing_by_date_type.weekend.original_price < data.pricing_by_date_type.weekend.price
      ) {
        ctx.addIssue({
          path: ["pricing_by_date_type", "weekend", "original_price"],
          code: "custom",
          message: "Weekend original price cannot be smaller than weekend price.",
        });
      }
    }
  })
}

export const NoShowTimeTicketSchema = (eventDetailData: ISimpleEventDetail) => {
  const ticketSchema = (priceType: PriceType) => {
    return z.object({
      price_type: z.literal(priceType),
      category: languageSchema(eventDetailData.languages),
      type_name: languageSchema(eventDetailData.languages),
      short_description:languageSchema(eventDetailData.languages, true),
      selling_min_qty: z
      .number({
        invalid_type_error: "Quantity must be a number",
        required_error: "This field is required"
      })
      .min(1, {message:"Quantity must be equal or greater than 1"})
      .nonnegative({ message: "Quantity must be a positive number" }),
      selling_max_qty: z
      .number({
        invalid_type_error: "Quantity must be a number",
        required_error: "This field is required"
      })
      .min(1, {message:"Quantity must be equal or greater than 1"})
      .nonnegative({ message: "Quantity must be a positive number" }),
      selling_type: z.string().min(1, "This field is required"),
      original_price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      price: priceType === PriceType.PAID 
        ? z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .positive({message: "Price must be greater than 0"})
        .nonnegative({ message: "Price must be a positive number" })
        : z
        .number({
          invalid_type_error: "Price must be a number",
          required_error: "This field is required"
        })
        .nonnegative({ message: "Price must be a positive number" }),
      sell_from: z.string().min(1, "This field is required"),
      sell_to: z.string().min(1, "This field is required"),
      attendant_template: z.array(
        z.object({
          template_id: z.string().min(1, "This field is required"),
          name: z.object({
            en: z.string().optional(),
            vi: z.string().optional()
          }),
        })
      ).min(1, "This field is required"),
      hide_in_ui: z.boolean(),
      is_package: z.boolean(),
      is_soldout: z.boolean(),
      is_active: z.boolean(),
      ticket_phase: z.string(),
      total_for_sell: z.union([z.number().int().min(0, "Total for sell must be equal or greater than 0").nullable(), z.nan()]).optional(),
      selling_days: z.array(z.string()).min(1, "This field is required"),
      weekly_event_days: z.array(z.string()).min(1, "This field is required"),
      sold_out_before: z.string().optional(),
      detailed_ticket_information: languageSchema(eventDetailData.languages, true),
      pricing_by_date_type: z.object({
        holiday: z.object({
          price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
          original_price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
        }),
        weekend: z.object({
          price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
          original_price: priceType === PriceType.PAID 
            ? z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .positive({message: "Price must be greater than 0"})
              .nonnegative({ message: "Price must be a positive number" })
            : z
              .number({
                invalid_type_error: "Price must be a number",
                required_error: "This field is required"
              })
              .nonnegative({ message: "Price must be a positive number" }),
        }),
      }).optional(),
    })
  };

  return z.discriminatedUnion("price_type",[
    ticketSchema(PriceType.FREE),
    ticketSchema(PriceType.PAID)
  ])
  .refine((data) => moment(data.sell_from).isSameOrBefore(moment(data.sell_to)), {
    message: "End date cannot be earlier than start date.",
    path: ["sell_to"],
  })
  .refine((data) => data.selling_min_qty <= data.selling_max_qty, {
    message: "Max quantity cannot be smaller than min quantity.",
    path: ["selling_max_qty"],
  })
  .refine((data) => data.original_price >= data.price, {
    message: "Original price cannot be smaller than sell price.",
    path: ["original_price"],
  })
  .superRefine((data, ctx) => {
    if (data.selling_max_qty < data.selling_min_qty) {
      ctx.addIssue({
        path: ["selling_max_qty"],
        code: "custom",
        message: "Selling max quantity cannot be smaller than Selling min quantity",
      });
    }
    if (eventDetailData.date_pricing?.enabled && data.pricing_by_date_type) {
      if (
        typeof data.pricing_by_date_type.holiday?.original_price === "number" &&
        typeof data.pricing_by_date_type.holiday?.price === "number" &&
        data.pricing_by_date_type.holiday.original_price < data.pricing_by_date_type.holiday.price
      ) {
        ctx.addIssue({
          path: ["pricing_by_date_type", "holiday", "original_price"],
          code: "custom",
          message: "Holiday original price cannot be smaller than holiday price.",
        });
      }
      if (
        typeof data.pricing_by_date_type.weekend?.original_price === "number" &&
        typeof data.pricing_by_date_type.weekend?.price === "number" &&
        data.pricing_by_date_type.weekend.original_price < data.pricing_by_date_type.weekend.price
      ) {
        ctx.addIssue({
          path: ["pricing_by_date_type", "weekend", "original_price"],
          code: "custom",
          message: "Weekend original price cannot be smaller than weekend price.",
        });
      }
    }
  })
};
